import { Button } from '@/components/ui/button';
import { ArrowLeft, Pencil, Copy, Trash2, ExternalLink, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface EntityDetailsHeaderProps {
	/** The entity icon/avatar component */
	icon: React.ReactNode;
	/** The entity name/title */
	name: React.ReactNode;
	/** Status badge component (e.g., Active/Inactive) */
	statusBadge?: React.ReactNode;
	/** Entity type name (e.g., "Active policy", "Business customer") */
	entityTypeName?: string;
	/** Optional subtitle shown between name and entity type */
	subtitle?: React.ReactNode;
	/** Last updated text (e.g., "Updated 2 days ago") */
	lastUpdated?: string;
	/** Entity ID for action handlers */
	entityId?: string;
	/** Edit handler */
	onEdit?: (id: string) => void;
	/** Clone handler */
	onClone?: (id: string) => void;
	/** Delete handler */
	onDelete?: (id: string) => void;
	/** Open in new window handler */
	onOpenInNewWindow?: (id: string) => void;
	/** Close button handler (for mobile) */
	onClose?: () => void;
	/** Back button handler (for nested modal navigation) */
	onBack?: () => void;
	/** Show back button */
	showBackButton?: boolean;
	/** Show close button */
	showCloseButton?: boolean;
	/** Additional custom action */
	renderAction?: () => React.ReactNode;
	/** Custom class name for the container */
	className?: string;
}

/**
 * Shared header component for entity details - used in both modals and side panels.
 * Displays entity icon, name, status, and action buttons in a consistent layout.
 */
export function EntityDetailsHeader({
	icon,
	name,
	statusBadge,
	entityTypeName,
	subtitle,
	lastUpdated,
	entityId,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton,
	showCloseButton,
	renderAction,
	className = ''
}: EntityDetailsHeaderProps) {
	const { t } = useTranslation('common');

	return (
		<div className={cn('flex items-start gap-4', className)}>
			{showBackButton && onBack && (
				<Button
					variant="outline"
					size="sm"
					onClick={onBack}
					className="h-8 w-8 p-0 mt-1 shadow-sm"
					title={t('buttons.back')}
				>
					<ArrowLeft className="w-4 h-4" />
				</Button>
			)}
			{/* Avatar/Icon */}
			{icon}
			{/* Name, Status, and Meta */}
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2.5 flex-wrap">
					<h2 className="text-xl font-semibold text-foreground truncate leading-tight">
						{name}
					</h2>
					{statusBadge}
				</div>
				{subtitle && (
					<div className="text-sm text-muted-foreground mt-0.5">
						{subtitle}
					</div>
				)}
				{(entityTypeName || lastUpdated) && (
					<div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
						{entityTypeName && <span>{entityTypeName}</span>}
						{entityTypeName && lastUpdated && <span className="text-border">|</span>}
						{lastUpdated && <span>{lastUpdated}</span>}
					</div>
				)}
			</div>
			{/* Action buttons */}
			<div className="flex items-center gap-0.5 flex-shrink-0">
				{renderAction?.()}
				{entityId && onOpenInNewWindow && (
					<>
						<Button
							size="sm"
							variant="ghost"
							onClick={() => onOpenInNewWindow(entityId)}
							className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
							title={t('buttons.openInNewWindow', 'Open in New Window')}
						>
							<ExternalLink className="w-4 h-4" />
						</Button>
						<div className="w-px h-5 bg-border mx-1" />
					</>
				)}
				{entityId && onEdit && (
					<Button
						size="sm"
						variant="ghost"
						onClick={() => onEdit(entityId)}
						className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
						title={t('buttons.edit')}
					>
						<Pencil className="w-4 h-4" />
					</Button>
				)}
				{entityId && onClone && (
					<Button
						size="sm"
						variant="ghost"
						onClick={() => onClone(entityId)}
						className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
						title={t('buttons.clone')}
					>
						<Copy className="w-4 h-4" />
					</Button>
				)}
				{entityId && onDelete && (
					<Button
						size="sm"
						variant="ghost"
						onClick={() => onDelete(entityId)}
						className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
						title={t('buttons.delete')}
					>
						<Trash2 className="w-4 h-4" />
					</Button>
				)}
				{showCloseButton && onClose && (
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="text-muted-foreground h-8 w-8 hover:text-foreground hover:bg-muted rounded-lg transition-colors ml-1"
						title={t('buttons.close')}
					>
						<X className="w-4 h-4" />
					</Button>
				)}
			</div>
		</div>
	);
}
